import { PlanogramBayNonCircular } from "@CommonTypes/merchflow/pog/bay";
import { PlanogramContainerCommonNonCircular } from "@CommonTypes/merchflow/pog/container";
import type {
  PlanogramItem,
  PlanogramItemCommonNonCircular,
  UnrangedPlanogramItem,
  UnrangedPlanogramItemNonCircular,
} from "@CommonTypes/merchflow/pog/item";
import { latestPogVersion, PogNonCircular } from "../types/merchflow/pog/pog";
import { PlanogramShelfNonCircular } from "../types/merchflow/pog/shelf";
import { PegProduct } from "@CommonTypes/merchflow/pog/pegboard";

export const adapterPlanogramItemToPlanogramItemCommon = (
  item: PlanogramItem | UnrangedPlanogramItem,
) => {
  // Keep this object and the interface aligned so new optional fields fail compilation too.
  const result = {
    uniqueId: item.uniqueId,
    id: item.id,
    productCode: item.productCode,
    name: item.name,
    upc: item.upc,
    brand: item.brand,
    variant: item.variant,
    categoryCode: item.categoryCode,
    subCategory: item.subCategory,
    size: item.size,
    unitWeight: item.unitWeight ?? null,
    trayWeight: item.trayWeight ?? null,
    caseWeight: item.caseWeight ?? null,
    color: item.color,
    weightUom: item.weightUom,
    profit: item.profit,
    quantity: item.quantity,
    price: item.price,
    salesAmount: item.salesAmount,
    orientation: item.orientation,
    validOrientations: [...item.validOrientations],
    merchandisingStyle: item.merchandisingStyle,
    overhangAsUnit: item.overhangAsUnit,
    shelfType: item.shelfType,
    unitWidth: item.unitWidth,
    unitHeight: item.unitHeight,
    unitDepth: item.unitDepth,
    trayWidth: item.trayWidth,
    trayHeight: item.trayHeight,
    trayDepth: item.trayDepth,
    caseWidth: item.caseWidth,
    caseHeight: item.caseHeight,
    caseDepth: item.caseDepth,
    displayWidth: item.displayWidth,
    displayHeight: item.displayHeight,
    displayDepth: item.displayDepth,
    dividerWidth: item.dividerWidth,
    dividerHeight: item.dividerHeight,
    noOfUnitsInCase: item.noOfUnitsInCase,
    unitsCaseWide: item.unitsCaseWide,
    unitsCaseDeep: item.unitsCaseDeep,
    unitsCaseHigh: item.unitsCaseHigh,
    noOfUnitsInTray: item.noOfUnitsInTray,
    unitsTrayWide: item.unitsTrayWide,
    unitsTrayDeep: item.unitsTrayDeep,
    unitsTrayHigh: item.unitsTrayHigh,
    squeezeWidthPct: item.squeezeWidthPct,
    squeezeHeightPct: item.squeezeHeightPct,
    squeezeDepthPct: item.squeezeDepthPct,
    squeezeWidth: item.squeezeWidth,
    squeezeHeight: item.squeezeHeight,
    squeezeDepth: item.squeezeDepth,
    overhangSqueezeDepthFlag: item.overhangSqueezeDepthFlag,
    cdt1: item.cdt1,
    cdt1Priority: item.cdt1Priority,
    cdt2: item.cdt2,
    cdt2Priority: item.cdt2Priority,
    cdt3: item.cdt3,
    cdt3Priority: item.cdt3Priority,
    facings: item.facings,
    minFacings: item.minFacings,
    maxFacings: item.maxFacings,
    facingsRows: item.facingsRows,
    minFacingsRows: item.minFacingsRows,
    maxFacingsRows: item.maxFacingsRows,
    maxFacingsRowsStated: item.maxFacingsRowsStated,
    nestingWidth: item.nestingWidth,
    nestingHeight: item.nestingHeight,
    nestingDepth: item.nestingDepth,
    unitsDeep: item.unitsDeep,
    dividerNeed: item.dividerNeed ? { ...item.dividerNeed } : null,
    loaderNeed: item.loaderNeed ? { ...item.loaderNeed } : null,
    minOnShelf: item.minOnShelf,
    minDaysOfSupply: item.minDaysOfSupply,
    maxDaysOfSupply: item.maxDaysOfSupply,
    zCappingFlag: item.zCappingFlag,
    zCappedUnits: item.zCappedUnits,
    xCappedUnits: item.savedCapUnits("x"),
    yCappedUnits: item.savedCapUnits("y"),
    xCapNum: item.xCapNum,
    xCapOrientation: item.xCapOrientation,
    yCapNum: item.yCapNum,
    yCapOrientation: item.yCapOrientation,
    zCapNum: item.zCapNum,
    zCapOrientation: item.zCapOrientation,
    inCoreRange: item.inCoreRange,
    newItemFlag: item.newItemFlag,
    newItemStoreCodes: item.newItemStoreCodes ? [...item.newItemStoreCodes] : undefined,
    dndFlag: item.dndFlag,
    spaceMix: item.spaceMix,
    ranking: item.ranking,
    rangedInBiggerOrSmallerStores: item.rangedInBiggerOrSmallerStores
      ? { ...item.rangedInBiggerOrSmallerStores }
      : undefined,
    ...(item.updateStatus ? { updateStatus: { ...item.updateStatus } } : {}),
  } satisfies PlanogramItemCommonNonCircular;

  type MissingKeys = Exclude<keyof PlanogramItemCommonNonCircular, keyof typeof result>;
  type ExtraKeys = Exclude<keyof typeof result, keyof PlanogramItemCommonNonCircular>;

  const missingKeysCheck: MissingKeys extends never ? true : never = true;
  const extraKeysCheck: ExtraKeys extends never ? true : never = true;

  // Ensure the result matches the interface exactly, failing compilation if it doesn't.
  void missingKeysCheck;
  void extraKeysCheck;

  return result;
};

type LegacyPegboardItem = {
  uniqueId: string;
  holes: Array<{
    holeNoLeft: number;
    holeNoTop: number;
  }>;
  width: number;
  height: number;
  pegboard: string;
};

const createPegProductFromLegacyPegboardItem = (item: LegacyPegboardItem): PegProduct => ({
  uniqueId: item.uniqueId,
  id: "",
  productCode: "",
  name: "",
  upc: "",
  brand: "",
  variant: "",
  categoryCode: "",
  subCategory: "",
  size: "",
  unitWeight: null,
  trayWeight: null,
  caseWeight: null,
  color: "",
  profit: null,
  quantity: 1,
  price: 0,
  salesAmount: 0,
  unitWidth: item.width ?? 0,
  unitHeight: item.height ?? 0,
  unitDepth: 0,
  squeezeWidth: false,
  squeezeWidthPct: 0,
  squeezeHeight: false,
  squeezeHeightPct: 0,
  squeezeDepth: false,
  squeezeDepthPct: 0,
  cdt1: "",
  cdt1Priority: 0,
  cdt2: "",
  cdt2Priority: 0,
  cdt3: "",
  cdt3Priority: 0,
  facings: 1,
  minFacings: 1,
  maxFacings: 1,
  minOnShelf: 1,
  minDaysOfSupply: 0,
  maxDaysOfSupply: 0,
  inCoreRange: false,
  newItemFlag: false,
  newItemStoreCodes: undefined,
  dndFlag: false,
  spaceMix: 0,
  ranking: 0,
});

/** "4.1" -> [4, 1]; a missing or unreadable version reads as [0, 0] (oldest). */
const parsePogVersion = (version: string | undefined | null): [number, number] => {
  const [major, minor] = String(version ?? "")
    .split(".")
    .map((part) => Number(part));
  return [Number.isFinite(major) ? major : 0, Number.isFinite(minor) ? minor : 0];
};

export const isPogVersionBelow = (version: string | undefined | null, target: string) => {
  const [major, minor] = parsePogVersion(version);
  const [targetMajor, targetMinor] = parsePogVersion(target);
  return major < targetMajor || (major === targetMajor && minor < targetMinor);
};

/**
 * The pre-DEV-3516 PSA import's signature on an item: a non-zero divider
 * width and height equal to the display width and height x100.
 */
export const holdsDisplaySizeAsDivider = (item: {
  dividerWidth?: number | null;
  dividerHeight?: number | null;
  displayWidth?: number | null;
  displayHeight?: number | null;
}) => {
  const dividerWidth = Number(item.dividerWidth ?? 0);
  const dividerHeight = Number(item.dividerHeight ?? 0);
  if (!dividerWidth && !dividerHeight) return false;
  return (
    Math.abs(dividerWidth - Number(item.displayWidth ?? 0) * 100) < 0.5 &&
    Math.abs(dividerHeight - Number(item.displayHeight ?? 0) * 100) < 0.5
  );
};

export const pogVersionAdapter = (pog: PogNonCircular) => {
  const sourceVersion = pog.version;
  pog.version = latestPogVersion;

  // 2.13 - add unranged containers
  if (pog.unrangedContainers === undefined) {
    pog.unrangedContainers = [];
  }
  // 2.9 - add relaxConstraint
  if (pog.relaxedConstraints === undefined) {
    pog.relaxedConstraints = [];
  }
  // 2.5 - add containers
  if (pog.containers === undefined) {
    pog.containers = [];
  }
  // Aesthetics score: a plan made before it was stored was never scored, so it
  // reads null ("Not scored") rather than a blank that looks like a zero.
  if (pog.metrics == null) {
    pog.metrics = {
      returnOnSpace: null,
      coreRange: null,
      shelfAlignment: null,
      dosMos: null,
      aesthetics: null,
    };
  } else if (pog.metrics.aesthetics === undefined) {
    pog.metrics.aesthetics = null;
  }
  // 3.3 - delete planogram width and height
  if ("width" in pog.planogram && "height" in pog.planogram) {
    delete pog.planogram.width;
    delete pog.planogram.height;
  }

  const upgradePegboard = (pegboard: {
    uniqueId: string;
    placements?: unknown[]; // new name
    items?: LegacyPegboardItem[]; // old name
  }) => {
    // 3.5 - rename items to placements and expand placement payload
    if (pegboard.placements === undefined) {
      pegboard.placements = (pegboard.items ?? []).map((legacyItem) => ({
        uniqueId: legacyItem.uniqueId,
        pegboard: legacyItem.pegboard,
        holes: legacyItem.holes.map((hole) => ({
          holeNoLeft: hole.holeNoLeft,
          holeNoTop: hole.holeNoTop,
          depth: 0,
        })),
        pegProduct: createPegProductFromLegacyPegboardItem(legacyItem),
      }));
    }

    if (pegboard.items !== undefined) {
      delete pegboard.items;
    }
  };

  const upgradeBay = (bay: PlanogramBayNonCircular) => {
    // 2.2 - add pegboards
    if (bay.pegboards === undefined) {
      bay.pegboards = [];
    }

    bay.pegboards.forEach(upgradePegboard);

    // 2.7 - remove hardcoded bayNo
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (bay.bayNo !== undefined) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      delete bay["bayNo"];
    }

    // 2.10 - order shelves from top to bottom
    bay.shelves.sort((shelf1, shelf2) => (shelf1.notchNo > shelf2.notchNo ? -1 : 1));

    // 3.3 - swap coordinates to width and height
    if ("xLeft" in bay && "xRight" in bay && "yBottom" in bay && "yTop" in bay) {
      // @ts-ignore
      bay.width = bay.xRight - bay.xLeft;
      // @ts-ignore
      bay.height = bay.yTop - bay.yBottom;

      delete bay.xLeft;
      delete bay.xRight;
      delete bay.yBottom;
      delete bay.yTop;
    }

    // 3.4 - add maxShelfHeight and maxProductHead
    if (bay.maxProductHead === undefined && bay.maxShelfHeight === undefined) {
      bay.maxProductHead = 0;
      bay.maxShelfHeight = 0;
    }

    // 4.7 - add splitAfter. False keeps the old reading: one unbroken run, so
    // a pog written before splits existed plans exactly as it always did.
    if (bay.splitAfter === undefined) {
      bay.splitAfter = false;
    }
  };

  const upgradeShelf = (shelf: PlanogramShelfNonCircular) => {
    // 2.3 - add grillHeight
    if (shelf.grillHeight === undefined) {
      shelf.grillHeight = 0;
    }

    // 2.7 - remove hardcoded shelfNo
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (shelf.shelfNo !== undefined) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      delete shelf["shelfNo"];
    }

    // 2.11 - add variantRestrictions
    if (shelf.variantRestrictions === undefined) {
      shelf.variantRestrictions = [];
    }

    // 3.7 - add weightCapacity (no data source carries a real limit yet)
    if (shelf.weightCapacity === undefined) {
      shelf.weightCapacity = null;
    }

    // 3.9 - add effectiveWeightLimit. Null, not a default: an older pog was
    // never judged against a cap, and inventing one here would put a verdict on
    // a shelf nothing ever measured.
    if (shelf.effectiveWeightLimit === undefined) {
      shelf.effectiveWeightLimit = null;
    }

    // 3.10 - add deadSpans. Empty keeps the old reading: a shorter
    // merchandisable width is a dead strip at the right end.
    if (shelf.deadSpans === undefined) {
      shelf.deadSpans = [];
    }

    // 4.3 - add shelf dividers. Null: an older pog never recorded any.
    if (shelf.dividers === undefined) {
      shelf.dividers = null;
    }

    // 4.4 - add the hand override copy.
    shelf.dividersSetByHand ??= null;

    // 4.5 - add the spring loader depth and the rule's decision. Null: an
    // older pog never recorded one.
    shelf.loaderDepth ??= null;
    shelf.loaderDecision ??= null;
    // With 4.5: the keep switch kept this shelf's fixture dividers (DEV-3517).
    shelf.dividersKeptFromFixture ??= null;
  };

  const upgradeItem = (item: PlanogramItemCommonNonCircular) => {
    // 4.1 - add placement defaults required by item geometry
    item.validOrientations ??= ["FRONT"];
    item.orientation ??= item.validOrientations[0] ?? "FRONT";
    item.facingsRows ??= 1;
    item.facings ??= item.facingsRows;

    // 2.1 - add profit
    if (item.profit === undefined) {
      item.profit = null;
    }
    // 2.4 - add dividers
    if (item.dividerWidth === undefined) {
      item.dividerWidth = 0;
    }
    if (item.dividerHeight === undefined) {
      item.dividerHeight = 0;
    }
    // 4.3 - before 4.3 the PSA product import wrote the product's display
    // width/height x100 into these (DEV-3516), and pogs copied them from the
    // product master. Keeping them would overflow every shelf once layout
    // counts dividers. Only that signature is zeroed: divider sizes a data
    // feed stated (e.g. 711's) are real and stay.
    if (isPogVersionBelow(sourceVersion, "4.3") && holdsDisplaySizeAsDivider(item)) {
      item.dividerWidth = 0;
      item.dividerHeight = 0;
    }

    // 2.14 - add squeeze dimensions
    if (item.squeezeWidth === undefined) {
      item.squeezeWidth = false;
    }
    if (item.squeezeHeight === undefined) {
      item.squeezeHeight = false;
    }
    if (item.squeezeDepth === undefined) {
      item.squeezeDepth = false;
    }

    // 2.15 - add spaceMix
    if (item.spaceMix === undefined) {
      item.spaceMix = 0;
    }

    // 3.1 - add product ranking
    if (item.ranking === null || item.ranking === undefined) {
      item.ranking = 0;
    }

    // 3.2 - separate squeeze dimension object
    // @ts-ignore
    if ("squeezeDimension" in item) {
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      item.squeezeWidth = item.squeezeDimension.width;
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      item.squeezeHeight = item.squeezeDimension.height;
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      item.squeezeDepth = item.squeezeDimension.depth;
      delete item.squeezeDimension;
    }

    // 3.8 — stated consumer-unit weight. Older pins on this branch used `weight`.
    if (item.unitWeight === undefined) {
      const legacy = item as { weight?: number | null };
      item.unitWeight = legacy.weight ?? null;
    }
    if (item.weightUom === undefined) {
      item.weightUom = null;
    }

    // 3.6 - add capping properties
    if (item.xCapNum === undefined) {
      item.xCapNum = 0;
    }
    if (item.xCapOrientation === undefined) {
      item.xCapOrientation = "FRONT";
    }
    if (item.yCapNum === undefined) {
      item.yCapNum = 0;
    }
    if (item.yCapOrientation === undefined) {
      item.yCapOrientation = "FRONT";
    }
    if (item.zCapNum === undefined) {
      item.zCapNum = 0;
    }
    if (item.zCapOrientation === undefined) {
      item.zCapOrientation = "FRONT";
    }

    // 3.7 - add unitsDeep (null = derive depth facings from shelf geometry, as before)
    if (item.unitsDeep === undefined) {
      item.unitsDeep = null;
    }
    // 4.4 - add dividerNeed (null: no divider policy judged this item)
    item.dividerNeed ??= null;
    // 4.5 - add loaderNeed (null: no loader rule judged this item)
    item.loaderNeed ??= null;

    // 4.0 - add nesting. Zero, not null: "does not nest" is a measurement,
    // and legacy JSON can carry an explicit null.
    item.nestingWidth ??= 0;
    item.nestingHeight ??= 0;
    item.nestingDepth ??= 0;

    // 4.6 - a stated ceiling is the file's merchYMax as-is. Import used to take
    // the vertical caps off it, so they go back on. Not idempotent, so gated on
    // the stored version.
    if (
      isPogVersionBelow(sourceVersion, "4.6") &&
      item.maxFacingsRowsStated === true &&
      item.yCapNum > 0
    ) {
      item.maxFacingsRows += item.yCapNum;
    }
  };

  const upgradeUnrangedItem = (unrangedItem: UnrangedPlanogramItemNonCircular) => {
    upgradeItem(unrangedItem);

    // 2.6 - add potential shelves
    if (unrangedItem.potentialShelves == null) {
      unrangedItem.potentialShelves = [];
    }

    // 2.8 - add reasons
    if (unrangedItem.reasons === undefined) {
      unrangedItem.reasons = [];

      // @ts-ignore
      if (unrangedItem.reason !== undefined) {
        // @ts-ignore
        unrangedItem.reasons.push(unrangedItem.reason);
        // @ts-ignore
        delete unrangedItem.reason;
      }
    }
  };

  const upgradeContainer = (container: PlanogramContainerCommonNonCircular) => {
    // 2.5 - add container shelf type
    if (container.shelfType === undefined) {
      container.shelfType = "REGULAR";
    }

    // 2.12 - add sales to containers
    if (container.sales === undefined) {
      container.sales = 0;
    }

    // 3.0 - replace label with cdts
    if ("label" in container && typeof container.label === "string") {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const cdts = container.label.split(",");
      // If 3, we asumme it was s2
      if (cdts.length === 3) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        container.cdts = [{ cdt0: cdts[0].trim(), cdt1: cdts[1].trim(), cdt2: cdts[2].trim() }];
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        container.cdts = cdts.map((cdt0) => ({ cdt0 }));
      }
    }
  };

  for (const bay of pog.planogram.bays) {
    upgradeBay(bay);

    for (const shelf of bay.shelves) {
      upgradeShelf(shelf);

      for (const item of shelf.items) {
        upgradeItem(item);
      }
    }
  }

  for (const unrangedItem of pog.unrangedItems) {
    upgradeUnrangedItem(unrangedItem);
  }

  // Deleted items are persisted too, and never pass through a class constructor's defaults.
  for (const deletedItem of pog.deletedItems ?? []) {
    upgradeItem(deletedItem);
  }

  for (const container of pog.containers) {
    upgradeContainer(container);
  }

  for (const container of pog.unrangedContainers) {
    upgradeContainer(container);
  }

  return pog;
};
